import { useCallback, useState } from 'react'

/**
 * The layer toggles every multi-site map surface keeps.
 *
 * Linear: MYD-486.
 *
 * This block existed verbatim in two places: `LocationsMapPanel` and
 * `EvaluationSites`. It was left duplicated on purpose while there was only one
 * real consumer of a shared version, because extracting for a hypothetical
 * second caller is how you get an abstraction shaped by nothing. There are now
 * two, so it moves here.
 *
 * Deliberately NOT included: `showOtherRetailers`. The Locations map has one
 * anchor and the evaluation Sites tab has one per candidate, so what that
 * toggle means differs between them. Only the state that is genuinely identical
 * lives here.
 *
 * `enabledRadii` starts empty: nothing is drawn until the user asks for a
 * radius. That matters because the ring selection also bounds which Other
 * Retailers show, and a surface that silently filtered them on first paint
 * would look like it had lost them.
 */
export function useMapLayerState() {
	const [showMarkers, setShowMarkers] = useState(true)
	const [enabledRadii, setEnabledRadii] = useState<number[]>([])
	const [showOverlaps, setShowOverlaps] = useState(false)
	const [showDemographics, setShowDemographics] = useState(false)

	const toggleRadius = useCallback((r: number) => {
		setEnabledRadii(prev =>
			prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r].sort((a, b) => a - b),
		)
	}, [])

	const toggleMarkers = useCallback(() => setShowMarkers(v => !v), [])
	const toggleOverlaps = useCallback(() => setShowOverlaps(v => !v), [])
	const toggleDemographics = useCallback(() => setShowDemographics(v => !v), [])

	return {
		showMarkers,
		toggleMarkers,
		enabledRadii,
		toggleRadius,
		showOverlaps,
		toggleOverlaps,
		showDemographics,
		toggleDemographics,
	}
}

export default useMapLayerState
